// Vendor Libs
import React from 'react';
import PropTypes from 'prop-types';
import {forOwn, get, split, startCase, trim} from 'lodash';
import classnames from 'classnames';
import {isEmail} from 'validator';

// Components
import Button from '../core/button';
import Checkbox from '../core/checkbox';
import ChipsInput from '../core/chip-input';
import Form from '../core/form';
import Input from '../core/input';
import Select from '../core/select';
import SubmitButton from '../core/submit-button';
import Textarea from '../core/textarea';

// Styles
import './network-form.scss';

function NetworkForm({className, editing, formName, onReset}) {
    const getCustomFieldProps = (fieldName) => {
        const customFieldProps = {
            freewheelNetworkName: {
                validate: [validateFreeWheelField],
                visible: shouldRenderFreeWheelField,
            },
            mrmProdId: {
                validate: [validateFreeWheelField, 'numeric'],
                visible: shouldRenderFreeWheelField,
            },
            mrmTestId: {
                validate: [validateFreeWheelField, 'numeric'],
                visible: shouldRenderFreeWheelField,
            },
            notificationEmail: {
                validate: [validateNotificationEmail],
            },
        };

        return customFieldProps[fieldName];
    };

    const getFieldProps = (fieldName, fieldType) => ({
        className: fieldType === 'select' ? 'network-form--select' : 'network-form--input',
        label: startCase(fieldName),
        name: fieldName,
        ...getCustomFieldProps(fieldName),
    });

    const handleCancelClick = (event) => {
        event && event.preventDefault && event.preventDefault();

        onReset();
    };

    const shouldRenderFreeWheelField = (formData) => get(formData, 'adDecisioningSystem') === 'FreeWheel';

    const validateFreeWheelField = (_formData, _name, value) =>
        (!value || value === '' ? 'This field cannot be empty when Ad Decisioning System is FreeWheel' : undefined);

    const validateNotificationEmail = (_formData, _name, value) => {
        let validationMessage;
        const emails = split(value, ',');

        forOwn(emails, (email) => {
            if (trim(email) && !isEmail(trim(email))) {
                validationMessage = 'Notification email list is invalid';

                return false;
            }
        });

        return validationMessage;
    };

    const getFormProps = () => ({
        className: classnames('network-form', className),
        editing: !!editing,
        fixedPositionedResponse: true,
        formLabel: 'Network',
        name: formName,
        onReset: () => handleCancelClick(),
        resetOnSubmit: !editing,
        submitData: true,
    });

    return (
        <Form {...getFormProps()}>
            <div className="network-form--input-fields">
                <Input {...getFieldProps('name')} validate={['required']} />
                <Input {...getFieldProps('alternateName')} />
                <Textarea {...getFieldProps('description')} />
                <Checkbox {...getFieldProps('allowed')} uncheckedValue="false" value="true" />
                <Checkbox {...getFieldProps('fastPath')} uncheckedValue="false" value="true" />
                <Checkbox {...getFieldProps('tmsAutomappingEnabled')} uncheckedValue="false" value="true" />
                <Checkbox
                    {...getFieldProps('crossServiceMappingEnabled')}
                    label="Cross Service Mapping"
                    uncheckedValue="false"
                    value="true"
                />
                <Checkbox {...getFieldProps('bingeable')} uncheckedValue="false" value="true" />
                <Select className="network-form--select" label="Ad Decisioning System" name="adDecisioningSystem">
                    <option value="">Choose a System...</option>
                    <option value="FreeWheel">FreeWheel</option>
                </Select>
                <Input {...getFieldProps('mrmTestId')} label="Programmer MRM Test ID" />
                <Input {...getFieldProps('mrmProdId')} label="Programmer MRM Production ID" />
                <Input {...getFieldProps('freewheelNetworkName')} />
                <Checkbox {...getFieldProps('secondaryAdProviderEnabled')} uncheckedValue="false" value="true" />
                <Textarea {...getFieldProps('notificationEmail')} />
                <ChipsInput {...getFieldProps('callSigns')} />
                <Input {...getFieldProps('caid')} label="CAID" />
                <Input {...getFieldProps('csidSiteSectionPrefix')} label="CSID Site Section Prefix" />
                <Input {...getFieldProps('csidSiteSectionSuffix')} label="CSID Site Section Suffix" />
                <Input {...getFieldProps('sfidSiteSectionPrefix')} label="SFID Site Section Prefix" />
                <Input {...getFieldProps('sfidSiteSectionSuffix')} label="SFID Site Section Suffix" />
                <Checkbox
                    {...getFieldProps('adInsertionEnabled')}
                    label="AD Insertion Enabled"
                    uncheckedValue="false"
                    value="true"
                />
                <Checkbox
                    {...getFieldProps('selfContentOwnerEnabled')}
                    label="Self Content Owner"
                    uncheckedValue="false"
                    value="true"
                />
                <Checkbox
                    {...getFieldProps('fwCaidPrefixEnabled')}
                    label="Is fw Caid Prefix Enabled"
                    uncheckedValue="false"
                    value="true"
                />
            </div>
            <div className="network-form--controls">
                <SubmitButton label="Save" />
                <Button className="network-form--cancel-button" onClick={handleCancelClick}>
                    Cancel
                </Button>
            </div>
        </Form>
    );
}

NetworkForm.propTypes = {
    className: PropTypes.string,
    editing: PropTypes.bool,
    formName: PropTypes.string,
    onReset: PropTypes.func,
};

export default NetworkForm;
